import { memo, type FC } from "react";

import type {
  AttributeInput,
  Maybe,
  MoneyV2,
} from "@shopify/hydrogen-react/storefront-api-types";

import { Badge } from "@components/Badge";
import { AddToCartButton } from "@components/Button/AddToCartButton.data";
import { Heading } from "@components/Heading";
import { Image } from "@components/Image";
import { Price } from "@components/Price";
import { Text } from "@components/Text";
import { VariantSelectorManager } from "@components/VariantSelector/VariantSelectorManager.data";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  type CartProduct,
  type ProductHeroSettings,
  ThemeMode,
} from "@root/tablescape/state";
import { type CartProductVariant } from "@root/tablescape/state/cart";
import { type TTailwindString, tw } from "classnames";

interface Props {
  attributes: AttributeInput[];
  badgeText: Maybe<string>;
  buttonText?: Maybe<string>;
  compareAtPrice?: Maybe<MoneyV2>;
  currentVariant: CartProductVariant;
  price: Partial<MoneyV2>;
  product: CartProduct;
  products: CartProduct[];
  selectVariant: (variant: CartProductVariant["id"]) => void;
  title: Maybe<string>;
  variants: CartProductVariant[];
}

const styles = {
  container: tw(
    "relative",
    "box-border",
    "w-full",
    "rounded",
    "border-2",
    "border-solid",
    "border-primary",
    "bg-background-secondary",
    "p-4",
    "text-primary",
    "mt-2",
    "mb-8"
  ),
  title: tw("mb-2"),
  productTitle: tw("mb-1"),
  priceBadgeContainer: tw(
    "mb-4",
    "flex",
    "flex-col",
    "md:mb-0",
    "md:flex-row",
    "md:items-center"
  ),
  badgeContainer: tw("flex", "md:ml-4", "w-fit"),
  priceContainer: tw("mb-1", "flex", "md:mb-0"),
  content: tw("flex", "flex-col", "flex-1", "overflow-hidden", "justify-start"),
  imageContainer: tw(
    "mr-3",
    "h-[104px]" as TTailwindString,
    "w-[104px]" as TTailwindString
  ),
  contentWrapper: tw("mb-4", "flex"),
  buttonContainer: tw("md:pl-30"),
};

export const ProductHighlightedUpsellViewBase: FC<Props> = ({
  attributes,
  badgeText,
  buttonText,
  currentVariant,
  price,
  product,
  products,
  selectVariant,
  title,
  variants,
}) => {
  const {
    settings: { intelligemsEnabled, theme },
  } = useSectionSettings<ProductHeroSettings>();
  return (
    <div className={styles.container} data-theme={ThemeMode.LIGHT}>
      {title && (
        <Text as="title2" className={styles.title}>
          {title}
        </Text>
      )}
      <div className={styles.contentWrapper}>
        {currentVariant?.imageUrl && (
          <div className={styles.imageContainer}>
            <Image
              alt={currentVariant.title}
              height={96}
              src={currentVariant.imageUrl}
              width={96}
            />
          </div>
        )}
        <div className={styles.content}>
          <div>
            {product && (
              <Heading as="h6" className={styles.productTitle}>
                {product.title}
              </Heading>
            )}
            {product && (
              <div className={styles.priceBadgeContainer}>
                <div className={styles.priceContainer}>
                  <Price
                    as="body2"
                    compareAtPrice={currentVariant.compareAtPrice}
                    price={price}
                  />
                </div>
                {badgeText && (
                  <Badge
                    className={styles.badgeContainer}
                    size="sm"
                    type="primary"
                  >
                    {badgeText}
                  </Badge>
                )}
              </div>
            )}
          </div>

          <VariantSelectorManager
            activeProduct={product}
            context="productFormSlider"
            displayUnavailableVariants={false}
            hideProductDetails={true}
            selectedVariant={currentVariant}
            setSelectedVariant={selectVariant}
            size="mini"
          />
        </div>
      </div>
      <div className={styles.buttonContainer}>
        <AddToCartButton
          attributes={attributes}
          {...(buttonText && { buttonTextOverride: buttonText })}
          buttonTypeOverride="secondary"
          isIntelligemsEnabled={intelligemsEnabled}
          products={products}
          theme={theme}
          variants={variants}
        />
      </div>
    </div>
  );
};

export const ProductHighlightedUpsellView = memo(
  ProductHighlightedUpsellViewBase
);
